import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Typography, Avatar, IconButton, Button, useTheme, useMediaQuery } from '@mui/material';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { CallAdd } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { between, colCenter } from '@ui/style/style';
import { StyledMenu } from '@ui/components/common/Menu';
import { useMemo, useState } from 'react';
import NewContactDialogDrawer from './NewContact';
import { MoreVert } from '@mui/icons-material';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@application/store/useStore';
import { deletePartnerContactPersonAsync } from '@application/features/partnerContactPerson/partnerContactPersonThunk';
import { hasPermission } from '@application/helpers/permission-helper';
const ContactsTab = () => {
    // Mobile menu state
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [actionMenuRowId, setActionMenuRowId] = useState(null);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteTargetRow, setDeleteTargetRow] = useState(null);
    const [deleteTargetName, setDeleteTargetName] = useState('');
    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };
    const handleActionMenuOpen = (event, rowId) => {
        setActionMenuAnchorEl(event.currentTarget);
        setActionMenuRowId(rowId);
    };
    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setActionMenuRowId(null);
    };
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canUpdatePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Update');
    const getInitials = (name) => {
        if (!name)
            return '';
        const parts = name.trim().split(' ').filter(Boolean);
        if (parts.length === 0)
            return '';
        if (parts.length === 1)
            return parts[0][0]?.toUpperCase() ?? '';
        return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
    };
    const eventGridColumns = [
        {
            field: 'name',
            headerName: t('sponsorships.name'),
            flex: 0.5,
            minWidth: 180,
            renderCell: (params) => {
                const name = params.value || params.row.eventName || '';
                const initials = getInitials(name);
                return (_jsxs(Box, { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, children: [_jsx(Avatar, { variant: 'rounded', src: params.row.profilePictureUrl || undefined, sx: {
                                width: 40,
                                height: 40,
                                fontSize: '14px',
                                backgroundColor: theme.palette.background.default,
                                color: theme.palette.action.active
                            }, children: initials }), _jsxs(Box, { display: 'flex', flexDirection: 'column', children: [_jsx(Typography, { variant: 'body2', fontWeight: 400, noWrap: true, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', children: name }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', noWrap: true, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' })] })] }));
            }
        },
        {
            field: 'email',
            headerName: t('sponsorships.email'),
            flex: 0.5,
            minWidth: 60,
            renderCell: (params) => (_jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', children: _jsx(Typography, { variant: 'body2', noWrap: true, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', children: params.value }) }))
        },
        {
            field: 'phone',
            headerName: t('sponsorships.phone'),
            flex: 0.5,
            minWidth: 100,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', noWrap: true, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', children: params.value }))
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (_jsxs(_Fragment, { children: [_jsx(IconButton, { size: 'small', onClick: (e) => handleActionMenuOpen(e, params.row.id), children: _jsx(MoreVert, { fontSize: 'small' }) }), actionMenuRowId === params.row.id && (_jsx(StyledMenu, { anchorEl: actionMenuAnchorEl, open: Boolean(actionMenuAnchorEl), onClose: handleActionMenuClose, actions: [
                            {
                                text: 'Edit',
                                group: true,
                                disabled: !canUpdatePartners,
                                to: () => {
                                    if (canUpdatePartners) {
                                        const contact = (partner?.contactPersons ?? []).find((c) => c.id === params.row.id) ??
                                            null;
                                        setSelectedContact(contact);
                                        setIsEdit(true);
                                        handleNewContactOpen();
                                        handleActionMenuClose();
                                    }
                                }
                            },
                            {
                                text: 'Delete',
                                group: true,
                                disabled: !canUpdatePartners,
                                to: () => {
                                    if (canUpdatePartners) {
                                        const contact = (partner?.contactPersons ?? []).find((c) => c.id === params.row.id);
                                        setConfirmDialogOpen(true);
                                        setDeleteTargetRow(String(params.row.id));
                                        setDeleteTargetName(contact?.name || '');
                                        handleActionMenuClose();
                                    }
                                }
                            }
                        ] }))] }))
        }
    ];
    const dispatch = useAppDispatch();
    const { partner } = useSelector((state) => state.partner);
    const [selectedContact, setSelectedContact] = useState(null);
    const contactRows = useMemo(() => (partner?.contactPersons ?? []).map((p, index) => ({
        id: p.id || String(index),
        name: (p.name && p.name.trim()) || `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
        email: p.email,
        phone: p.phoneNo,
        profilePictureUrl: p.profilePictureUrl
    })), [partner?.contactPersons]);
    // New Event dialog/drawer state
    const [newContactOpen, setNewContactOpen] = useState(false);
    const handleNewContactOpen = () => setNewContactOpen(true);
    const handleNewContactClose = () => {
        setNewContactOpen(false);
        setIsEdit(false);
        setSelectedContact(null);
    };
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { rows: contactRows, maxHeight: 'calc(100dvh - 168px)', sx: { height: 'calc(100dvh - 168px)' }, columns: eventGridColumns, showFilters: false, showHeaderControls: false, showNewHeaderControls: true, customButton: _jsxs(Box, { sx: {
                        ...(!mobileSearchOpen && between),
                        alignItems: isMobile ? undefined : 'center',
                        pb: '12px'
                    }, children: [!mobileSearchOpen && (_jsx(Box, { sx: { ...colCenter, gap: 1 }, children: _jsxs(Typography, { variant: 'h5', color: 'text.primary', children: [contactRows.length, ' ', contactRows.length === 1
                                        ? t('sponsorships.contactPerson')
                                        : t('sponsorships.contactPersons')] }) })), _jsx(Box, { sx: {
                                ...colCenter,
                                gap: 1.5,
                                pb: 0
                            }, children: isMobile ? (_jsx(_Fragment, { children: _jsx(Box, { display: 'flex', alignItems: 'center', gap: '12px', children: _jsx(IconButton, { disabled: !canUpdatePartners, onClick: () => {
                                            if (canUpdatePartners) {
                                                setIsEdit(false);
                                                handleNewContactOpen();
                                            }
                                        }, sx: {
                                            color: theme.palette.primary.darker,
                                            border: `1px solid ${theme.palette.primary.darker}`
                                        }, children: _jsx(CallAdd, { size: 24, variant: 'Outline' }) }) }) })) : (_jsx(_Fragment, { children: _jsx(Box, { display: 'flex', alignItems: 'center', gap: '12px', children: _jsx(Button, { size: 'large', variant: 'outlined', color: 'primary', startIcon: _jsx(CallAdd, { size: 24, variant: 'Outline' }), disabled: !canUpdatePartners, onClick: () => {
                                            if (canUpdatePartners) {
                                                setIsEdit(false);
                                                setSelectedContact(null);
                                                handleNewContactOpen();
                                            }
                                        }, children: t('sponsorships.newContactPerson') }) }) })) })] }) }), _jsx(NewContactDialogDrawer, { isEdit: isEdit, open: newContactOpen, onClose: handleNewContactClose, partnerId: partner?.id ?? '', initialContact: selectedContact }), _jsx(ConfirmationDialog, { open: confirmDialogOpen, onClose: () => {
                    setConfirmDialogOpen(false);
                    setDeleteTargetRow(null);
                }, title: t('sponsorships.deleteContactPersonTitle', { defaultValue: 'Delete Contact' }), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', onClick: () => {
                                setConfirmDialogOpen(false);
                                setDeleteTargetRow(null);
                                setDeleteTargetName('');
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, onClick: async () => {
                                if (partner?.id && deleteTargetRow) {
                                    try {
                                        await dispatch(deletePartnerContactPersonAsync({
                                            partnerId: partner.id,
                                            contactPersonId: String(deleteTargetRow)
                                        })).unwrap();
                                    }
                                    catch {
                                        // Toast handled in thunk
                                    }
                                }
                                setConfirmDialogOpen(false);
                                setDeleteTargetRow(null);
                                setDeleteTargetName('');
                            }, variant: 'contained', color: 'primary', children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 2, children: _jsx(Typography, { variant: 'body1', children: t('sponsorships.deleteContactPersonText', {
                            name: deleteTargetName || '',
                            defaultValue: 'Are you sure you want to delete "{{name}}"?'
                        }) }) }) })] }));
};
export default ContactsTab;
